import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, Plus, TrendingUp, MousePointerClick, Target, DollarSign, MapPin, CheckCircle2 } from "lucide-react";
import { sr } from "../../lib/sr";
import { auth } from "../../lib/api";

const STAFF = ["admin", "super_admin", "campaign_manager", "compliance_reviewer"];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const STATUS_BADGE = { LIVE: "sr-badge-green", AWAITING_APPROVAL: "sr-badge-amber", AWAITING_REVIEW: "sr-badge-amber", SUBMITTED: "sr-badge", ARCHIVED: "sr-badge-gray" };

function Stat({ label, value, accent }) {
  return (
    <div className="sr-card" style={{ flex: "1 1 150px", minWidth: 140 }}>
      <div className="sr-muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || "var(--navy)", marginTop: 4 }}>{value}</div>
    </div>
  );
}

function MetricsForm({ c, onSaved }) {
  const [m, setM] = useState({ spend: c.metrics?.spend ?? "", clicks: c.metrics?.clicks ?? "", impressions: c.metrics?.impressions ?? "", conversions: c.metrics?.conversions ?? "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      ["spend", "clicks", "impressions", "conversions"].forEach((k) => { if (m[k] !== "") body[k] = Number(m[k]); });
      await sr.googleAds.updateMetrics(c.id, body);
      toast.success("Results updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save results"); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border)" }} data-testid={`gads-metrics-${c.id}`}>
      {["spend", "clicks", "impressions", "conversions"].map((k) => (
        <div key={k} style={{ display: "grid", gap: 2 }}>
          <label className="sr-muted" style={{ fontSize: 11, textTransform: "capitalize" }}>{k}</label>
          <input className="sr-input" style={{ width: 96 }} type="number" value={m[k]} onChange={(e) => setM((s) => ({ ...s, [k]: e.target.value }))} data-testid={`gads-metric-${k}-${c.id}`} />
        </div>
      ))}
      <button className="sr-btn sr-btn-sm sr-btn-primary" disabled={busy} onClick={save} data-testid={`gads-metric-save-${c.id}`}>Save results</button>
    </div>
  );
}

export default function GoogleAds() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", budget_monthly: "", goal: "leads", keywords: "", locations: "", final_url: "" });
  const [busy, setBusy] = useState(false);
  const isStaff = STAFF.includes(auth.getUser()?.role);

  const load = () => sr.googleAds.summary().then(setData).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  const preview = useMemo(() => {
    const media = Number(form.budget_monthly || 0);
    const premium = Math.round(media * 0.15 * 100) / 100;
    return { media, premium, client: Math.round((media + premium) * 100) / 100 };
  }, [form.budget_monthly]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name the campaign"); return; }
    if (!(Number(form.budget_monthly) > 0)) { toast.error("Set a monthly media budget"); return; }
    setBusy(true);
    try {
      await sr.googleAds.createCampaign({
        name: form.name.trim(), budget_monthly: Number(form.budget_monthly), goal: form.goal,
        keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        locations: form.locations.split(",").map((s) => s.trim()).filter(Boolean),
        final_url: form.final_url.trim() || null,
      });
      toast.success("Google Search campaign created — submitted for setup");
      setForm({ name: "", budget_monthly: "", goal: "leads", keywords: "", locations: "", final_url: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create campaign"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load Google Search. Please try again.</p></div>;

  const t = data.totals || {};

  return (
    <div data-testid="google-ads-page">
      <div style={{ marginBottom: 6 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><Search size={12} /> Google Search</span>
        <h1 style={{ fontSize: 30 }}>Google Search Ads</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Run and measure Google search campaigns alongside Meta. SmartReach manages the Google account for you — a <b>{data.premium_pct}% premium</b> applies to Google media spend (you pay media × {(1 + data.premium_pct / 100).toFixed(2)}).</p>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "16px 0" }} data-testid="gads-totals">
        <Stat label="Campaigns" value={t.campaigns ?? 0} />
        <Stat label="Media budget / mo" value={money(t.media_budget)} />
        <Stat label={`Premium (${data.premium_pct}%)`} value={money(t.premium)} accent="#9A7412" />
        <Stat label="Client price / mo" value={money(t.client_price)} accent="var(--blue)" />
        <Stat label="Spend to date" value={money(t.spend)} />
        <Stat label="Clicks" value={(t.clicks ?? 0).toLocaleString()} />
        <Stat label="Conversions" value={(t.conversions ?? 0).toLocaleString()} accent="#067647" />
      </div>

      <div className="sr-card" style={{ marginBottom: 18 }} data-testid="gads-create">
        <div style={{ fontWeight: 800, color: "var(--navy)", marginBottom: 4 }}><Plus size={15} style={{ verticalAlign: -2 }} /> New Google Search campaign</div>
        <p className="sr-muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 12 }}>Tell us the budget and targeting — our team sets it up in the managed Google account and confirms when it's live.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
          <div style={{ display: "grid", gap: 3 }}>
            <label className="sr-label">Campaign name</label>
            <input className="sr-input" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Downtown lunch search" data-testid="gads-name" />
          </div>
          <div style={{ display: "grid", gap: 3 }}>
            <label className="sr-label">Monthly media budget</label>
            <input className="sr-input" type="number" value={form.budget_monthly} onChange={(e) => setForm((s) => ({ ...s, budget_monthly: e.target.value }))} placeholder="1500" data-testid="gads-budget" />
          </div>
          <div style={{ display: "grid", gap: 3 }}>
            <label className="sr-label">Goal</label>
            <select className="sr-input" value={form.goal} onChange={(e) => setForm((s) => ({ ...s, goal: e.target.value }))} data-testid="gads-goal">
              {["leads", "calls", "visits", "bookings", "orders", "sales"].map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div style={{ display: "grid", gap: 3 }}>
            <label className="sr-label">Final URL (optional)</label>
            <input className="sr-input" value={form.final_url} onChange={(e) => setForm((s) => ({ ...s, final_url: e.target.value }))} placeholder="https://yourbusiness.com" data-testid="gads-url" />
          </div>
          <div style={{ display: "grid", gap: 3 }}>
            <label className="sr-label">Keywords (comma-separated)</label>
            <input className="sr-input" value={form.keywords} onChange={(e) => setForm((s) => ({ ...s, keywords: e.target.value }))} placeholder="italian restaurant, pizza near me" data-testid="gads-keywords" />
          </div>
          <div style={{ display: "grid", gap: 3 }}>
            <label className="sr-label">Locations (comma-separated)</label>
            <input className="sr-input" value={form.locations} onChange={(e) => setForm((s) => ({ ...s, locations: e.target.value }))} placeholder="Dallas TX, Plano TX" data-testid="gads-locations" />
          </div>
        </div>
        {preview.media > 0 && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12, alignItems: "center" }} data-testid="gads-preview">
            <span className="sr-badge sr-badge-gray">Media {money(preview.media)}</span>
            <span className="sr-badge" style={{ background: "#FBEFD9", color: "#9A7412" }}>+ {data.premium_pct}% premium {money(preview.premium)}</span>
            <span className="sr-badge" style={{ background: "var(--blue-light)", color: "var(--blue)" }} data-testid="gads-preview-client">You pay {money(preview.client)}/mo</span>
          </div>
        )}
        <button className="sr-btn sr-btn-primary" style={{ marginTop: 14 }} disabled={busy} onClick={create} data-testid="gads-create-btn">{busy ? <Loader2 size={15} className="sr-spin" /> : <Plus size={15} />} Create campaign</button>
      </div>

      <div style={{ display: "grid", gap: 12 }} data-testid="gads-list">
        {data.campaigns.length === 0 ? (
          <div className="sr-card sr-empty" data-testid="gads-empty">
            <Search size={24} style={{ opacity: .4 }} />
            <p style={{ marginTop: 10 }}>No Google Search campaigns yet. Create one above to reach people actively searching for what you offer.</p>
          </div>
        ) : data.campaigns.map((c) => (
          <div key={c.id} className="sr-card" data-testid={`gads-campaign-${c.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontWeight: 800, color: "var(--navy)" }}>{c.name}</div>
                <div className="sr-muted" style={{ fontSize: 12, marginTop: 2 }}>Goal: {c.goal} · Google Search</div>
              </div>
              <span className={`sr-badge ${STATUS_BADGE[c.status] || "sr-badge"}`} data-testid={`gads-status-${c.id}`}>{(c.status || "").replace(/_/g, " ")}</span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10, fontSize: 13 }}>
              <span className="sr-badge sr-badge-gray"><DollarSign size={11} /> Media {money(c.budget_monthly)}/mo</span>
              <span className="sr-badge" style={{ background: "#FBEFD9", color: "#9A7412" }}>+{c.premium_pct}% {money(c.premium)}</span>
              <span className="sr-badge" style={{ background: "var(--blue-light)", color: "var(--blue)" }} data-testid={`gads-client-${c.id}`}>Client {money(c.client_price)}/mo</span>
            </div>
            {(c.keywords?.length || c.locations?.length) ? (
              <div className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>
                {c.keywords?.length ? <span><b>Keywords:</b> {c.keywords.slice(0, 8).join(", ")}{c.keywords.length > 8 ? "…" : ""}</span> : null}
                {c.locations?.length ? <span style={{ marginLeft: 10 }}><MapPin size={11} style={{ verticalAlign: -1 }} /> {c.locations.join(", ")}</span> : null}
              </div>
            ) : null}
            {(c.metrics && (c.metrics.spend || c.metrics.clicks)) ? (
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10, fontSize: 13 }} data-testid={`gads-results-${c.id}`}>
                <span><TrendingUp size={12} style={{ verticalAlign: -1 }} /> Spend <b>{money(c.metrics.spend)}</b></span>
                <span><MousePointerClick size={12} style={{ verticalAlign: -1 }} /> Clicks <b>{(c.metrics.clicks || 0).toLocaleString()}</b></span>
                {c.cpc != null && <span>CPC <b>{money(c.cpc)}</b></span>}
                {c.metrics.conversions != null && <span><Target size={12} style={{ verticalAlign: -1 }} /> Conv <b>{c.metrics.conversions}</b></span>}
                {c.cpa != null && <span>CPA <b>{money(c.cpa)}</b></span>}
              </div>
            ) : (
              <div className="sr-muted" style={{ fontSize: 12, marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}><CheckCircle2 size={13} style={{ opacity: .5 }} /> Results appear here once the campaign is live.</div>
            )}
            {isStaff && <MetricsForm c={c} onSaved={load} />}
          </div>
        ))}
      </div>
    </div>
  );
}
